import { useState, useEffect } from "react";
import { Box, Card, CardContent, Divider, Grid, Typography, useTheme } from "@mui/material"
import ProcesosService from "../../services/procesos.service";
import { useNavigate } from 'react-router-dom';
import { ButtonBasic, InputReadQuill } from "../../components";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const ProcesosScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [items, setItems] = useState(null)
    const actualizar = async() =>{
        await ProcesosService.getAll().then((resp)=>{
            setItems(resp)
        })
    }
    useEffect(() => {
        actualizar();
        document.title = `Procesos`;
    }, [])
    const onClickProceso = (idProceso) =>{
        navigate(`/procesos/${idProceso}`)
    }
    return (
        <Box sx={{width:'100%',height: '100%',overflow:'auto', background: isDarkMode ? '#1a2332' : '#f5f5f5'}} className='side'>
            <Grid container p={3} mb={20} spacing={3}>
                {/* Header con diseño mejorado */}
                <Grid item xs={12}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #0364ff 0%, #0247b5 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(3, 100, 255, 0.3)'
                            }}
                        >
                            <AccountTreeIcon sx={{ color: '#fff', fontSize: 32 }} />
                        </Box>
                        <Typography 
                            sx={{
                                color: isDarkMode ? '#e0e0e0' : '#15294b',
                                fontWeight: 700,
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Procesos
                        </Typography>
                    </Box>
                    <Divider 
                        sx={{ 
                            maxWidth: 600, 
                            mx: 'auto',
                            borderColor: isDarkMode ? '#90caf9' : '#0364ff',
                            borderWidth: 2
                        }} 
                    />
                </Grid>

                {/* Cards de procesos mejorados */}
                { items?.length > 0 && (
                    items?.map((it)=>(
                        <Grid item xs={12} sm={6} md={4} key={`proceso${it.id_pro}`}>
                            <Card 
                                sx={{
                                    background: isDarkMode ? '#2d3a52' : '#ffffff !important',
                                    boxShadow: '0 2px 8px rgba(15, 41, 75, 0.08)',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e5e7eb',
                                    transition: 'all 0.3s ease',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 24px rgba(3, 100, 255, 0.15)',
                                        borderColor: isDarkMode ? '#90caf9' : '#0364ff',
                                        '& .proceso-icon': {
                                            transform: 'scale(1.1) rotate(5deg)'
                                        },
                                        '& .proceso-button': {
                                            background: isDarkMode ? '#90caf9' : '#0364ff',
                                            color: '#fff',
                                            '& .MuiSvgIcon-root': {
                                                transform: 'translateX(4px)'
                                            }
                                        }
                                    }
                                }}
                            >
                                {/* Header con gradiente */}
                                <Box
                                    sx={{
                                        background: 'linear-gradient(135deg, #0364ff 0%, #0247b5 100%)',
                                        p: 2.5,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -50,
                                            right: -50,
                                            width: 150,
                                            height: 150,
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: '50%'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                                        <Box
                                            className="proceso-icon"
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2,
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                backdropFilter: 'blur(10px)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            <AccountTreeIcon sx={{ color: '#fff', fontSize: 24 }} />
                                        </Box>
                                        <Typography 
                                            sx={{
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: '1.15rem',
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {it?.nombre_pro}
                                        </Typography>
                                    </Box>
                                </Box>

                                <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    {/* Descripción del proceso */}
                                    <Box 
                                        sx={{ 
                                            minHeight: 100,
                                            maxHeight: 150,
                                            overflow: 'auto',
                                            mb: 2,
                                            flexGrow: 1,
                                            p: 1.5,
                                            background: isDarkMode ? '#1F2A40' : '#f8f9fa',
                                            borderRadius: 2,
                                            border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e5e7eb',
                                            '& .ql-editor': {
                                                padding: '0 !important',
                                                fontSize: '0.875rem',
                                                color: isDarkMode ? '#b0bec5' : '#374151'
                                            }
                                        }}
                                    >
                                        <InputReadQuill template={it?.descripcion_pro || 'Sin descripción'} />
                                    </Box>

                                    {/* Botón de acción */}
                                    <ButtonBasic
                                        className="proceso-button"
                                        textButton="Ver Proceso"
                                        fullWidth
                                        disableElevation
                                        sx={{
                                            borderRadius: 2,
                                            background: isDarkMode ? '#1F2A40' : '#fff',
                                            color: isDarkMode ? '#90caf9' : '#0364ff',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            py: 1.25,
                                            border: isDarkMode ? '2px solid #90caf9' : '2px solid #0364ff',
                                            transition: 'all 0.3s ease',
                                            '& .MuiSvgIcon-root': {
                                                transition: 'transform 0.3s ease'
                                            }
                                        }}
                                        iconEnd={<ArrowForwardIcon />}
                                        onClick={()=>onClickProceso(it?.id_pro)}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    )
}

export default ProcesosScreen